import { memo, useContext, useEffect, useRef, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { FilterBarValueContext } from './context';
import { Input, Select, SelectProps, Tag } from 'antd';
import { css, cx } from '@emotion/css';
import { getStoreCount, StoreScopes } from 'components/StoreScopes';
import { AdminStore } from 'services/api/admin';
import { useScopedStores } from '@knowledge/common/components/ItemSelectV2/useScopedStores';

const FilterBar = memo(
  (props: {
    className?: string;
    storeSelectPlacement?: SelectProps['placement'];
    storeScopes?: General.StoreScope[];
  }) => {
    const { className, storeSelectPlacement, storeScopes } = props;
    const storeDataSource = useScopedStores(storeScopes);
    return (
      <div className={cx('flex h-full items-center', className)}>
        <div className="mr-[16px] min-w-[160px]">
          <StoreSelect placement={storeSelectPlacement} storeDataSource={storeDataSource} />
        </div>
        <div className="min-w-[264px]">
          <KeywordSelect />
        </div>
      </div>
    );
  },
);

const StoreSelect = memo(
  (props: { placement?: SelectProps['placement']; storeDataSource?: AdminStore[] }) => {
    const { placement = 'bottomLeft', storeDataSource } = props;
    const { t } = useTranslate();
    const [visible, setVisible] = useState(false);
    const { storeScopes, setStoreScopes } = useContext(FilterBarValueContext);

    return (
      <Select
        mode="multiple"
        showSearch={false}
        placeholder={t('remind.选择店铺')}
        value={storeScopes?.length ? ['placeholder'] : []}
        allowClear
        onClear={() => {
          setStoreScopes([]);
        }}
        tagRender={() => (
          <Tag>
            {t('department.已选x店铺', {
              x: getStoreCount(storeScopes || []),
            })}
          </Tag>
        )}
        open={visible}
        onDropdownVisibleChange={setVisible}
        placement={placement}
        dropdownMatchSelectWidth={false}
        dropdownRender={(_menu) =>
          visible ? (
            <StoreScopes
              allowEmpty
              supportWa={false}
              className={css`
                max-height: 95vh;
                overflow-y: auto;
              `}
              value={storeScopes}
              onCancel={() => setVisible(false)}
              showFooterTips={false}
              onChange={(storeScopes) => {
                setStoreScopes(storeScopes);
              }}
              storeDataSource={storeDataSource?.length ? storeDataSource : undefined}
            />
          ) : (
            <></>
          )
        }
        className={css`
          width: 160px;
        `}
        dropdownClassName={dropdownClassName}
      />
    );
  },
);

const dropdownClassName = css`
  width: 950px !important;
  overflow-y: auto;
  padding-top: 0;
  padding-bottom: 0;
  color: rgba(0, 0, 0, 0.85);
`;

type KeywordType = 1 | 2 | 3 | 4;

const KeywordSelect = memo(() => {
  const { t } = useTranslate();
  const { keyword, keywordType, setKeyword, setKeywordType } = useContext(FilterBarValueContext);
  const keywordTypeLabelMap: Record<KeywordType, string> = {
    1: t('sider.商品名称'),
    2: t('sider.商品ID'),
    3: t('aide.主商品货号'),
    4: t('sider.SKU货号'),
  };
  const inputRef = useRef<any>(null);
  useEffect(() => {
    inputRef.current.input.value = keyword;
  }, [keyword]);
  return (
    <Input.Group
      compact
      className={css`
        flex: 1 0 259px;
        display: flex !important;
      `}
    >
      <Select
        value={keywordType}
        onChange={(value) => setKeywordType(value)}
        dropdownMatchSelectWidth={false}
        options={Object.entries(keywordTypeLabelMap).map(([value, label]) => {
          return {
            value: parseInt(value, 10),
            label,
          };
        })}
      />
      <Input.Search
        allowClear
        ref={inputRef}
        placeholder={t('trade.请输入')}
        onPressEnter={(e: any) => {
          setKeyword(e?.target?.value);
        }}
        onSearch={(value) => {
          setKeyword(value);
        }}
      />
    </Input.Group>
  );
});

export default FilterBar;
